/*
 * Keeps chat-history presentation and ordering rules independent from React so
 * session metadata can be interpreted consistently without component state.
 */
import { DateTime } from 'luxon';

export const SESSION_ID_FALLBACK_LENGTH = 18;
export const SESSION_TITLE_MAX_LENGTH = 80;
export const UNASSIGNED_ENTITY_ID = 'unassigned';

export interface ChatSession {
  sessionId: string;
  entityId?: string;
  title?: string;
  lastModifiedAt?: string;
}

export const getTimestamp = (lastModifiedAt: string | undefined): number | undefined => {
  if (!lastModifiedAt) return undefined;

  const timestamp = DateTime.fromISO(lastModifiedAt).toMillis();
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

export const getSessionTitle = (session: ChatSession): string => {
  if (session.title?.trim()) return session.title;
  if (session.sessionId.length <= SESSION_ID_FALLBACK_LENGTH) return session.sessionId;
  return `${session.sessionId.slice(0, SESSION_ID_FALLBACK_LENGTH)}…`;
};

export const getCampaignLabel = (
  session: ChatSession,
  campaignNames: ReadonlyMap<string, string>,
): string | undefined => {
  if (!session.entityId || session.entityId === UNASSIGNED_ENTITY_ID) return undefined;
  return campaignNames.get(session.entityId) ?? session.entityId;
};

export const compareSessions = (
  first: ChatSession,
  second: ChatSession,
  currentEntityId: string | undefined,
): number => {
  const firstIsCurrent = currentEntityId != null && first.entityId === currentEntityId;
  const secondIsCurrent = currentEntityId != null && second.entityId === currentEntityId;
  if (firstIsCurrent !== secondIsCurrent) return firstIsCurrent ? -1 : 1;

  const firstTimestamp = getTimestamp(first.lastModifiedAt);
  const secondTimestamp = getTimestamp(second.lastModifiedAt);
  if (firstTimestamp === undefined) return secondTimestamp === undefined ? 0 : 1;
  if (secondTimestamp === undefined) return -1;
  return secondTimestamp - firstTimestamp;
};

export const formatLastModifiedAt = (lastModifiedAt: string | undefined): string | undefined => {
  if (!lastModifiedAt) return undefined;

  const dateTime = DateTime.fromISO(lastModifiedAt);
  return dateTime.isValid ? dateTime.toLocaleString(DateTime.DATETIME_MED) : undefined;
};
